// Character Compare — pick two characters and see them head-to-head.

import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import animeConfig from '../config/animeConfig';
import QuizPageText from '../components/QuizPageText/QuizPageText';
import { comparePageText } from '../components/QuizPageText/quizPageText.data';
import characterProfiles from '../data/characterProfiles';
import CharacterPicker from '../components/CharacterPicker/CharacterPicker';
import './CharacterCompare.css';

const SORTED = [...characterProfiles].sort((a, b) => a.name.localeCompare(b.name));
const bountyNum = (c) => Number(String(c?.bounty || '').replace(/[^0-9]/g, '')) || 0;
const findBySlug = (slug) => characterProfiles.find((c) => c.slug === slug);

const Stat = ({ label, a, b, winnerSide }) => (
  <div className="cc-stat-row">
    <span className={`cc-stat-val ${winnerSide === 'a' ? 'win' : ''}`}>{a}</span>
    <span className="cc-stat-label">{label}</span>
    <span className={`cc-stat-val ${winnerSide === 'b' ? 'win' : ''}`}>{b}</span>
  </div>
);

const CharacterCompare = () => {
  const [slugA, setSlugA] = useState('monkey-d-luffy');
  const [slugB, setSlugB] = useState('roronoa-zoro');

  const a = useMemo(() => findBySlug(slugA), [slugA]);
  const b = useMemo(() => findBySlug(slugB), [slugB]);

  const bountyA = bountyNum(a);
  const bountyB = bountyNum(b);
  const bountyWinner = bountyA === bountyB ? null : bountyA > bountyB ? 'a' : 'b';
  const fmt = (n) => (n > 0 ? `฿${n.toLocaleString('en-US')}` : 'Unknown');

  // The same sea the rest of the archive sits on, under a dark wash so the
  // cards and the text keep their contrast.
  const seaStyle = {
    backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.86)), url(${animeConfig.onePiece.backgroundImage})`,
  };

  return (
    <>
      <Helmet>
        <title>Compare One Piece Characters — Head to Head | One Piece Trivia</title>
        <meta name="description" content="Compare any two One Piece characters side by side — bounty, crew, role, and Devil Fruit." />
        <link rel="canonical" href="https://www.onepiecetrivia.com/compare" />
        <meta property="og:title" content="Compare One Piece Characters" />
        <meta property="og:description" content="Put any two characters side by side — bounty, crew, role and Devil Fruit." />
        <meta property="og:url" content="https://www.onepiecetrivia.com/compare" />
      </Helmet>

      <div className="cc-container" style={seaStyle}>
        <h1 className="cc-title">Compare Characters</h1>
        <p className="cc-sub">Pick two and see them head-to-head.</p>

        <div className="cc-pickers">
          <CharacterPicker characters={SORTED} value={slugA} onChange={setSlugA} label="First character" />
          <span className="cc-vs">VS</span>
          <CharacterPicker characters={SORTED} value={slugB} onChange={setSlugB} label="Second character" />
        </div>

        {a && b && (
          <div className="cc-board">
            <div className="cc-heads">
              <div className="cc-head">
                <img src={a.image} alt={a.name} className="cc-img" />
                <span className="cc-name">{a.name}</span>
              </div>
              <div className="cc-head">
                <img src={b.image} alt={b.name} className="cc-img" />
                <span className="cc-name">{b.name}</span>
              </div>
            </div>

            <div className="cc-stats">
              <Stat label="Bounty" a={fmt(bountyA)} b={fmt(bountyB)} winnerSide={bountyWinner} />
              <Stat label="Crew" a={a.crew || '—'} b={b.crew || '—'} />
              <Stat label="Role" a={a.role || '—'} b={b.role || '—'} />
              <Stat label="Devil Fruit" a={a.devilFruit || 'None'} b={b.devilFruit || 'None'} />
              <Stat label="Fruit Type" a={a.devilFruitType || '—'} b={b.devilFruitType || '—'} />
            </div>
          </div>
        )}
      </div>
      <QuizPageText {...comparePageText} />
    </>
  );
};

export default CharacterCompare;
